// @ts-nocheck
export const STAGE = {
  SIGNIN: 'Signing in',
  INCOMING: 'Incoming call',
  OUTGOING: 'Outgoing call',
  AUDIO: 'Audio',
  OTHER: 'Other',
};

const E = (label, help, stage, tone = 'neutral') => ({ label, help, stage, tone });

export const SIP_EVENT_DICTIONARY = {
  // ---- signing in -------------------------------------------------------
  ws_connecting: E(
    'Connecting to phone server',
    'The agent’s browser started opening a connection to the phone server.',
    STAGE.SIGNIN,
  ),
  ws_connected: E(
    'Connected to phone server',
    'The connection to the phone server is open.',
    STAGE.SIGNIN,
    'good',
  ),
  ws_closed: E(
    'Connection closed',
    'The connection to the phone server was closed.',
    STAGE.SIGNIN,
  ),
  ws_error: E(
    'Could not reach the phone server',
    'The browser could not open or keep its connection. Usually a network, Wi-Fi, VPN or firewall problem on the agent’s side.',
    STAGE.SIGNIN,
    'bad',
  ),
  register_sent: E(
    'Signing the extension in',
    'The WebRTC phone asked the phone server to sign this extension in.',
    STAGE.SIGNIN,
  ),
  register_ok: E(
    'Extension is ready',
    'The extension signed in successfully — the agent can now receive calls.',
    STAGE.SIGNIN,
    'good',
  ),
  register_failed: E(
    'Extension sign-in failed',
    'The phone server refused the sign-in. Most often a wrong extension password, or the server did not reply in time.',
    STAGE.SIGNIN,
    'bad',
  ),
  unregister: E(
    'Extension signed out',
    'The extension was signed out of the phone server.',
    STAGE.SIGNIN,
  ),
  disconnect: E(
    'Disconnected from phone server',
    'The WebRTC phone closed its connection — normal when the agent logs out or closes the tab.',
    STAGE.SIGNIN,
  ),
  credentials_missing: E(
    'Extension details missing',
    'The agent profile has no extension number or password, so the WebRTC phone could not even try to sign in. Fix the agent’s telephony profile.',
    STAGE.SIGNIN,
    'bad',
  ),
  recovery_attempt: E(
    'Trying to reconnect',
    'The WebRTC phone noticed a problem and is reconnecting by itself.',
    STAGE.SIGNIN,
  ),
  recovery_ok: E(
    'Reconnected',
    'The WebRTC phone recovered on its own — the agent is back online.',
    STAGE.SIGNIN,
    'good',
  ),
  recovery_failed: E(
    'Reconnect failed',
    'The WebRTC phone could not recover by itself. The agent usually has to log out and log in again.',
    STAGE.SIGNIN,
    'bad',
  ),
  force_logout: E(
    'Agent was signed out automatically',
    'The system signed the agent out of WebRTC, normally after repeated connection problems.',
    STAGE.SIGNIN,
    'bad',
  ),

  // ---- incoming ---------------------------------------------------------
  invite_received: E(
    'Call reached the agent',
    'The agent’s browser actually received this call. If this line is missing for a call, the call never arrived at the agent and the problem is before the WebRTC phone.',
    STAGE.INCOMING,
    'good',
  ),
  ringing_local: E(
    'Agent’s phone rang',
    'The incoming call was shown and the ringtone started on the agent’s screen.',
    STAGE.INCOMING,
  ),
  auto_answer: E(
    'Answered automatically',
    'The call was picked up automatically because auto-answer was switched on.',
    STAGE.INCOMING,
  ),
  answer_clicked: E(
    'Agent clicked answer',
    'The agent pressed the answer button.',
    STAGE.INCOMING,
  ),
  answer_failed: E(
    'Answering failed',
    'The agent tried to answer but the call could not be picked up.',
    STAGE.INCOMING,
    'bad',
  ),
  declined: E(
    'Call declined',
    'The call was rejected — either by the agent or automatically (for example, the agent was on a break).',
    STAGE.INCOMING,
  ),
  cancel_received: E(
    'Caller hung up before it was answered',
    'The other side ended the call while it was still ringing.',
    STAGE.INCOMING,
  ),
  bye_received: E(
    'Other side ended the call',
    'The customer or the system hung up.',
    STAGE.INCOMING,
  ),

  // ---- outgoing ---------------------------------------------------------
  invite_sent: E(
    'Outgoing call started',
    'The agent started dialling this number.',
    STAGE.OUTGOING,
  ),
  progress_100: E(
    'Phone server accepted the call',
    'The phone server received the dial request and is working on it.',
    STAGE.OUTGOING,
  ),
  progress_180: E(
    'Ringing at the other end',
    'The number being called is ringing.',
    STAGE.OUTGOING,
  ),
  progress_183: E(
    'Ringback / early audio',
    'The network started sending audio (ringing tone or an announcement) before the call was answered.',
    STAGE.OUTGOING,
  ),
  place_call_failed: E(
    'Could not start the call',
    'The dial attempt was rejected. The status code below says why — busy, wrong number, or not allowed.',
    STAGE.OUTGOING,
    'bad',
  ),
  bye_sent: E(
    'Agent ended the call',
    'The agent hung up.',
    STAGE.OUTGOING,
  ),

  // ---- shared -----------------------------------------------------------
  answered_200: E(
    'Call connected',
    'The call was answered and both sides are now talking.',
    STAGE.OTHER,
    'good',
  ),
  call_ended: E(
    'Call ended',
    'The call finished.',
    STAGE.OTHER,
  ),

  // ---- audio ------------------------------------------------------------
  mic_permission_denied: E(
    'Microphone blocked',
    'The browser blocked the microphone, so the agent cannot be heard. The agent must allow microphone access for this site.',
    STAGE.AUDIO,
    'bad',
  ),
  getusermedia_failed: E(
    'Microphone not usable',
    'The microphone could not be opened — unplugged, used by another app, or the wrong device is selected.',
    STAGE.AUDIO,
    'bad',
  ),
  ice_failed: E(
    'Audio connection failed',
    'Signalling worked but the voice path could not be set up. Usually a firewall or restrictive network at the agent’s location.',
    STAGE.AUDIO,
    'bad',
  ),
  ice_disconnected: E(
    'Audio connection dropped',
    'The voice path dropped mid-call — typically an unstable internet connection.',
    STAGE.AUDIO,
    'bad',
  ),
  dtls_failed: E(
    'Secure audio setup failed',
    'The encrypted voice channel could not be established, so no audio can flow.',
    STAGE.AUDIO,
    'bad',
  ),
  no_remote_audio: E(
    'No audio received',
    'The call was connected but no sound arrived from the other side — the classic "I answered but I can’t hear anything".',
    STAGE.AUDIO,
    'bad',
  ),
  audio_flowing: E(
    'Audio is flowing',
    'Sound is arriving from the other side — the call has working audio.',
    STAGE.AUDIO,
    'good',
  ),

  // ---- misc -------------------------------------------------------------
  lib_error: E(
    'WebRTC warning',
    'The WebRTC phone reported an internal warning. Useful for support, not usually actionable by the agent.',
    STAGE.OTHER,
    'bad',
  ),
  rate_limited: E(
    'Some events were not recorded',
    'The WebRTC phone produced too many events in a short time, so a few were skipped to protect performance.',
    STAGE.OTHER,
  ),
};

const prettify = (t) =>
  String(t || 'Event')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());

export function describeEvent(type) {
  return (
    SIP_EVENT_DICTIONARY[type] || {
      label: prettify(type),
      help: '',
      stage: STAGE.OTHER,
      tone: 'neutral',
    }
  );
}

/**
 * Status codes in plain words. `kind` separates SIP responses from
 * WebSocket close codes — 403 means very different things in each.
 */
const SIP_CODES = {
  200: 'Accepted',
  100: 'Server is working on it',
  180: 'Ringing',
  183: 'Ringing with early audio',
  400: 'The request was malformed',
  401: 'Wrong extension password',
  403: 'Not allowed — check the extension or permissions',
  404: 'Number not found',
  407: 'Wrong extension password',
  408: 'The phone server did not reply in time',
  410: 'That number no longer exists',
  480: 'The person is unavailable',
  486: 'Busy',
  487: 'Cancelled before it was answered',
  488: 'The two sides could not agree on an audio format',
  500: 'Phone server error',
  502: 'Problem between phone servers',
  503: 'Service temporarily unavailable',
  504: 'The other side timed out',
  603: 'The call was declined',
};

const WS_CODES = {
  1000: 'Closed normally',
  1001: 'The page or server went away',
  1005: 'Closed without a reason',
  1006: 'Connection dropped unexpectedly — usually network, Wi-Fi, VPN or firewall',
  1011: 'Phone server error',
  1015: 'Secure connection (TLS) could not be established',
};

export function describeCode(code, kind) {
  const n = Number(code) || 0;
  if (!n) return null;
  if (kind === 'ws') return WS_CODES[n] || 'Connection closed with an unusual code';
  if (kind === 'media') return null;
  return SIP_CODES[n] || null;
}

/** Help text for the few technical fields we do show. */
export const FIELD_HELP = {
  extension: 'The agent’s WebRTC extension number.',
  remote_number: 'The other party on the call.',
  duration_ms: 'How long this step took.',
  code: 'The status the phone server replied with. Hover the plain-English text next to it.',
  sip_call_id: 'A unique reference for this call. Share it with support to look up the same call everywhere.',
  call_sid: 'The platform’s own call reference.',
  trace_id: 'Reference for this WebRTC session, used by support to group everything from one browser tab.',
  browser: 'The browser the agent was using.',
  os: 'The agent’s operating system.',
  net_type: 'The kind of internet connection the browser reported.',
};
